import React, { useEffect, useRef } from "react";

// Converts any Spotify URL variant into a spotify:type:id URI
function parseSpotifyUri(url) {
  if (!url) return null;
  if (url.startsWith("spotify:")) return url;

  // Handles:
  //   https://open.spotify.com/episode/ID
  //   https://open.spotify.com/embed/episode/ID
  //   https://open.spotify.com/show/ID  etc.
  const match = url.match(
    /open\.spotify\.com\/(?:embed\/)?([a-z]+)\/([a-zA-Z0-9]+)/,
  );
  if (match) return `spotify:${match[1]}:${match[2]}`;
  return null;
}

function SpotifyEpisodes({ data }) {
  const embedControllerRef = useRef(null);

  useEffect(() => {
    const providerName = data?.provider?.toLowerCase();
    if (providerName !== "spotify" || !data?.link) return;

    const uri = parseSpotifyUri(data.link);
    if (!uri) return;

    const element = document.getElementById("embed-iframe");
    if (!element) return;

    const initPlayer = (IFrameAPI) => {
      // Reset the container so the API can write a fresh iframe into it
      element.innerHTML = "";
      embedControllerRef.current = null;

      IFrameAPI.createController(element, { uri }, (EmbedController) => {
        embedControllerRef.current = EmbedController;
        if (data.isPlay) {
          EmbedController.play();
        }
      });
    };

    // If the Spotify API already loaded (e.g. second episode click)
    if (window.SpotifyIframeApi) {
      initPlayer(window.SpotifyIframeApi);
      return;
    }

    // Register the ready callback
    window.onSpotifyIframeApiReady = (IFrameAPI) => {
      window.SpotifyIframeApi = IFrameAPI;
      initPlayer(IFrameAPI);
    };

    // Dynamically load the Spotify IFrame API script if not already present
    if (
      !document.querySelector('script[src*="spotify.com/embed/iframe-api"]')
    ) {
      const script = document.createElement("script");
      script.src = "https://open.spotify.com/embed/iframe-api/v1";
      script.async = true;
      document.body.appendChild(script);
    }

    return () => {
      // Only clear the callback if the API hasn't resolved yet
      if (!window.SpotifyIframeApi) {
        window.onSpotifyIframeApiReady = null;
      }
    };
  }, [data?.provider, data?.link]);

  // Handle play/pause toggling on an already-initialised controller
  useEffect(() => {
    const controller = embedControllerRef.current;
    if (!controller) return;
    if (data.isPlay) {
      controller.play();
    } else {
      controller.pause();
    }
  }, [data.isPlay]);

  return <div id="embed-iframe" style={{ minHeight: 232 }} />;
}

export default SpotifyEpisodes;
